// Cohort machinery for the comparative widgets: scope->endpoint maps,
// rank/stats math, response flattening + row normalization, and the
// usePickedEntity / useCohortContext hooks that tie it together.
import { useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { fetchCohortByScope } from '@/reducers/qa-copilot/qa-copilot.actions'

import { PickedEntityValuesContext } from './dashboardContexts'
import { useScorecardNameById } from './format'

// ---- scopeMap ----
// Shared scope-keyed lookup tables for the comparative band widgets.
// Each widget on a scope tab (By Scorecard / By Campaign / By Agent)
// needs the same four mappings:
//   - which ClickHouse endpoint to fetch the cohort from,
//   - which row key identifies an entity in that response,
//   - which `filters.*` slice the picker writes its selection into,
//   - what to call the entity in user-facing copy.
// Co-locating them keeps the five widgets identical at the contact
// point with the rest of the app.

// Per-scope cohort endpoint. Each one must return rows that carry a
// pass-rate field AND a call-count field — the comparative widgets
// rank by pass rate and label by volume. `aggregate/callCount/by_user`
// only returns call_count (no pass rate), so we use
// `calls/scored_calls_by_user` for the agent scope which returns
// `passing_average` per user. `normalizeCohortRow` handles the
// shape differences.
export const ENDPOINT_BY_SCOPE = {
  agent: 'calls/scored_calls_by_user',
  scorecard: 'aggregate/multiple',
  tag: 'calls/scored_calls_by_tag',
}

export const IDENTITY_KEY_BY_SCOPE = {
  agent: 'user_id',
  scorecard: 'scorecard_config_sid',
  tag: 'tag_id',
}

export const FILTER_KEY_BY_SCOPE = {
  agent: 'agent_ids',
  scorecard: 'scorecard_config_sids',
  tag: 'tag_ids',
}

export const LABEL_BY_SCOPE = {
  agent: 'agent',
  scorecard: 'scorecard',
  tag: 'tag',
}

// ---- cohortMath ----
// Pure derivation helpers used by the comparative widgets. Kept in a
// separate file (no React, no Redux) so they're trivial to unit test
// and reusable across all three scope tabs.

// 1-indexed rank of `entityId` within `cohort` when sorted DESC by
// `getValue`. Uses standard competition ranking — equal values share
// a rank ("1, 2, 2, 4"). Returns null when not found.
export const rankOf = (cohort, entityId, getValue) => {
  if (!Array.isArray(cohort) || isEmpty(cohort)) {
    return null
  }
  const target = cohort.find((item) => item.id === entityId)
  if (!target) {
    return null
  }
  const targetValue = getValue(target)
  let rank = 1
  cohort.forEach((item) => {
    if (getValue(item) > targetValue) {
      rank += 1
    }
  })
  return { rank, total: cohort.length }
}

// avg / median / min / max for a list of numeric values. Returns
// null fields when empty so consumers can render a placeholder.
export const cohortStats = (values) => {
  if (!Array.isArray(values) || isEmpty(values)) {
    return { avg: null, median: null, min: null, max: null, count: 0 }
  }
  const sorted = [...values].sort((valueA, valueB) => valueA - valueB)
  const sum = sorted.reduce((acc, value) => acc + value, 0)
  const mid = Math.floor(sorted.length / 2)
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
  return {
    avg: sum / sorted.length,
    median,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    count: sorted.length,
  }
}

// Top-K strengths (entity > cohort avg) and top-K weaknesses
// (entity < cohort avg) by absolute delta. Skips keys missing from
// either side. Returns rows with `{id, entity, cohort, delta}`.
export const strengthsWeaknessesFrom = (entityRates, cohortRates, options = {}) => {
  const { strengthsN = 3, weaknessesN = 2 } = options
  const deltas = []
  Object.keys(entityRates).forEach((id) => {
    const entity = entityRates[id]
    const cohort = cohortRates[id]
    if (isNil(entity) || isNil(cohort)) {
      return
    }
    deltas.push({ id, entity, cohort, delta: entity - cohort })
  })
  const strengths = deltas
    .filter((row) => row.delta > 0)
    .sort((rowA, rowB) => rowB.delta - rowA.delta)
    .slice(0, strengthsN)
  const weaknesses = deltas
    .filter((row) => row.delta < 0)
    .sort((rowA, rowB) => rowA.delta - rowB.delta)
    .slice(0, weaknessesN)
  return { strengths, weaknesses }
}

// ---- normalizeCohortRow ----
// Maps a raw cohort row from the scorecard-service aggregate endpoints
// to the shape the comparative widgets consume. The three endpoints
// (by_user / multiple / by_tag) all return slightly different
// field names for "name" and "pass rate", so this normalizes them.
//
// Returns null when the row has no identity (so callers can drop it).

// Per-user rows from /calls/scored_calls_by_user split the call count
// into passed + failed, while per-tag and per-scorecard rows
// expose a flat call_count / total_calls. Sum the split fields when
// present so the agent scope reports the right volume.
const totalCallCount = (row) => {
  if (!isNil(row.call_count)) {
    return parseInt(row.call_count, 10)
  }
  if (!isNil(row.total_calls)) {
    return parseInt(row.total_calls, 10)
  }
  const passed = parseInt(row.passed_call_count ?? 0, 10)
  const failed = parseInt(row.failed_call_count ?? 0, 10)
  return passed + failed
}

export const normalizeCohortRow = (row, identityKey) => {
  const id = !isNil(row[identityKey]) ? String(row[identityKey]) : null
  if (isNil(id)) {
    return null
  }
  return {
    id,
    name: row.full_name || row.scorecard_config_name || row.tag_name || row[identityKey],
    passRate: parseFloat(row.passing_average ?? row.pass_rate ?? row.sid_average ?? 0),
    callCount: totalCallCount(row),
  }
}

// ---- flattenCohortResponse ----
// Normalize the three cohort endpoints' response shapes into a flat
// row array the comparative widgets can map over. The scorecard scope
// hits `aggregate/multiple` which returns a nested object keyed by
// sid, not a list -- without this transform the cohort silently
// collapsed to `[]` and the widget rendered "no data" forever.

const flattenScorecardAggregate = (data, scorecardNameById) =>
  Object.entries(data.aggregate_scores || {})
    .filter(([sid]) => sid !== 'total')
    .map(([sid, score]) => ({
      scorecard_config_sid: sid,
      passing_average: parseFloat(score) || 0,
      call_count: data.call_counts?.[sid] ?? 0,
      scorecard_config_name: scorecardNameById[sid] || sid,
    }))

export const flattenCohortResponse = (data, scope, scorecardNameById = {}) => {
  if (Array.isArray(data)) {
    return data
  }
  if (Array.isArray(data?.items)) {
    return data.items
  }
  if (scope === 'scorecard' && data?.aggregate_scores) {
    return flattenScorecardAggregate(data, scorecardNameById)
  }
  return []
}

// ---- usePickedEntity ----
// Active "picked entity" for a scope tab. The picker now stores its
// choice in per-tab state (exposed via PickedEntityValuesContext) so
// it is decoupled from the global filter bar. Focus widgets read it
// here; cohort widgets keep reading filters.scorecards (etc) for the
// comparison set.

export const usePickedEntity = (scope) => {
  const all = useContext(PickedEntityValuesContext)
  return all?.[scope] || null
}

// ---- useCohortContext ----
// Picked entity's place in its cohort (rank / avg / delta). Redux-
// backed; fetchCohortByScope dedupes concurrent widget mounts into
// one request. Returns { isAvailable: false } on Overview (no picker).

const COMPARATIVE_SCOPES = new Set(['scorecard', 'agent', 'tag'])

// What the cohort's normalized `passRate` actually measures per
// scope: the scorecard endpoint (aggregate/multiple) returns AVERAGE
// QA SCORE, not a pass rate. Consumers must label accordingly or the
// KPI silently switches metrics on the By Scorecard tab.
export const METRIC_LABEL_BY_SCOPE = {
  agent: 'pass rate',
  tag: 'pass rate',
  scorecard: 'avg QA score',
}

export const useCohortContext = (scope) => {
  const dispatch = useDispatch()
  const filters = useSelector((state) => state.qaCopilot.filters)
  const cohortSlice = useSelector((state) => state.qaCopilot.data.cohort)
  const loading = useSelector((state) => state.qaCopilot.loading.cohort)
  const scorecardNameById = useScorecardNameById()
  const pickedEntity = usePickedEntity(scope)

  const isAvailable = COMPARATIVE_SCOPES.has(scope)

  // One dispatcher per consumer, but the action no-ops when the
  // request is already in flight or its result already in redux --
  // so N parallel mounts produce 1 network request, not N.
  useEffect(() => {
    if (!isAvailable) {
      return
    }
    dispatch(fetchCohortByScope(scope))
  }, [dispatch, isAvailable, scope, filters])

  // Only consume cohort data when it belongs to the current scope --
  // a stale slice from the previous tab shouldn't leak into the new
  // tab's rendering while the new fetch is still pending.
  const raw = isAvailable && cohortSlice?.scope === scope ? cohortSlice.raw : null

  const normalized = useMemo(() => {
    if (!raw) {
      return []
    }
    const identityKey = IDENTITY_KEY_BY_SCOPE[scope]
    const rows = flattenCohortResponse(raw, scope, scorecardNameById)
    return rows
      .map((row) => normalizeCohortRow(row, identityKey))
      .filter((row) => !isNil(row) && Number.isFinite(row.passRate))
  }, [raw, scope, scorecardNameById])

  const selectedId = !isNil(pickedEntity?.value) ? String(pickedEntity.value) : null
  const selectedRow = normalized.find((row) => row.id === selectedId)
  const stats = useMemo(() => cohortStats(normalized.map((row) => row.passRate)), [normalized])
  const passRateRank = useMemo(
    () => rankOf(normalized, selectedId, (row) => row.passRate),
    [normalized, selectedId]
  )
  const volumeRank = useMemo(
    () => rankOf(normalized, selectedId, (row) => row.callCount),
    [normalized, selectedId]
  )

  const passDelta = !isNil(stats.avg) && selectedRow ? selectedRow.passRate - stats.avg : null

  const metricLabel = METRIC_LABEL_BY_SCOPE[scope] || 'pass rate'

  return {
    isAvailable,
    loading,
    pickedEntity,
    metricLabel,
    metricIsPassRate: metricLabel === 'pass rate',
    selectedId,
    cohortCount: stats.count,
    cohortAvg: stats.avg,
    cohortMax: stats.max,
    selectedPassRate: selectedRow?.passRate ?? null,
    selectedCallCount: selectedRow?.callCount ?? null,
    passDelta,
    passRateRank,
    volumeRank,
    normalized,
    stats,
  }
}
